import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { bdt, bn, computeTotals, colorName, PHONE_RE, normalizePhone, metaContents, metaContentIds } from "@/lib/shop";
import { trackBoth } from "@/lib/meta";
import { buildUtmPayload } from "@/lib/utm";
import Reveal from "@/components/Reveal";
import ProductSelect from "@/components/ProductSelect";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TEXT_FIELDS = [
    { key: "name", label: "আপনার নাম", placeholder: "যেমন: আরিফুল ইসলাম", testid: "order-form-name-input", autoComplete: "name" },
    { key: "phone", label: "মোবাইল নম্বর", placeholder: "01XXXXXXXXX", testid: "order-form-phone-input", type: "tel", inputMode: "tel", autoComplete: "tel" },
];

function StepTitle({ n, title, hint }) {
    return (
        <div className="flex items-start gap-3 mb-4">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#A84320] text-[#FBF9F5] text-sm font-bold shrink-0 mt-0.5">
                {bn(n)}
            </span>
            <div>
                <h3 className="font-serif text-lg font-semibold text-[#1E1B18] leading-snug">{title}</h3>
                {hint && <p className="text-xs text-[#8A8278] mt-0.5">{hint}</p>}
            </div>
        </div>
    );
}

export default function OrderForm({ lines, setLines, zone, setZone, stock }) {
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", phone: "", address: "", email: "", note: "" });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState("");
        const idemRef = useRef(crypto.randomUUID());
    const zoneManual = useRef(false);
    const checkoutRef = useRef(null);
    const checkoutTracked = useRef(false);
    const leadTracked = useRef(false);

    const totals = computeTotals(lines, zone);

    useEffect(() => {
        const el = checkoutRef.current;
        if (!el) return;
        const ob = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !checkoutTracked.current) {
                    checkoutTracked.current = true;
                    trackBoth("InitiateCheckout", {
                        value: totals.subtotal || 900, currency: "BDT",
                        num_items: totals.qty || 1,
                                        content_ids: metaContentIds(lines), content_type: "product",
                                        content_name: "খাদি পাঞ্জাবি",
                                        content_category: "পাঞ্জাবি",
                                        contents: metaContents(lines),
                                    });
                                    ob.disconnect();
                                }
            },
            { threshold: 0.25 },
        );
        ob.observe(el);
        return () => ob.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (leadTracked.current) return;
        if (form.name.trim() && PHONE_RE.test(normalizePhone(form.phone)) && form.address.trim().length >= 8) {
            leadTracked.current = true;
                    trackBoth("Lead", {
                        value: totals.subtotal || 900, currency: "BDT",
                                content_ids: metaContentIds(lines), content_type: "product",
                        content_name: "খাদি পাঞ্জাবি",
                        content_category: "পাঞ্জাবি",
                        num_items: totals.qty || 1,
                                contents: metaContents(lines),
                            });
                        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form]);

    const setField = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
        if (k === "address" && !zoneManual.current) {
            setZone(/ঢাকা|dhaka/i.test(v) ? "inside_dhaka" : "outside_dhaka");
        }
    };

    function validate() {
        const e = {};
        if (!form.name.trim()) e.name = "আপনার নাম লিখুন";
        if (!PHONE_RE.test(normalizePhone(form.phone))) e.phone = "সঠিক মোবাইল নম্বর দিন (যেমন 01XXXXXXXXX)";
        if (form.address.trim().length < 8) e.address = "বাসা/হোল্ডিং, রোড, এলাকা, থানা ও জেলাসহ সম্পূর্ণ ঠিকানা লিখুন";
        if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) e.email = "সঠিক ইমেইল ঠিকানা দিন অথবা খালি রাখুন";
        if (!zone) e.zone = "ডেলিভারি এলাকা নির্বাচন করুন";
        if (lines.length === 0) {
            e.items = "কমপক্ষে ১টি পাঞ্জাবি সিলেক্ট করুন (সাইজে ট্যাপ করুন)";
        } else if (stock) {
            const need = {};
            lines.forEach((l) => { need[l.color] = (need[l.color] || 0) + l.qty; });
            for (const [color, n] of Object.entries(need)) {
                const s = stock[color];
                if (s && !s.available) {
                    e.items = `${colorName(color)} রংটি এই মুহূর্তে স্টকে নেই — অন্য রং বাছাই করুন।`;
                    break;
                }
                if (s && s.quantity !== null && s.quantity < n) {
                    e.items = `${colorName(color)} রঙের স্টক সীমিত — পরিমাণ কমিয়ে দিন।`;
                    break;
                }
            }
        }
        return e;
    }

    async function submit(ev) {
        ev.preventDefault();
        if (submitting) return;
        setServerError("");
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length) return;
        setSubmitting(true);
        try {
            const utm = buildUtmPayload();
            const res = await fetch(`${API}/api/orders`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name.trim(),
                    phone: form.phone.trim(),
                    email: form.email.trim() || null,
                    address: form.address.trim(),
                    note: form.note.trim() || null,
                    country: "বাংলাদেশ",
                    items: lines.map((l) => ({ color: l.color, size: l.size, qty: l.qty })),
                    delivery_zone: zone,
                    idempotency_key: idemRef.current,
                    event_source_url: window.location.href,
                    utm,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setServerError(data.detail || "অর্ডার সম্পন্ন করা যায়নি। আবার চেষ্টা করুন।");
                return;
            }
            sessionStorage.setItem("sm_order", JSON.stringify(data));
            idemRef.current = crypto.randomUUID();
            navigate("/order-success");
        } catch {
            setServerError("নেটওয়ার্ক সমস্যা হয়েছে। ইন্টারনেট সংযোগ পরীক্ষা করে আবার চেষ্টা করুন — আপনার তথ্য হারাবে না।");
        } finally {
            setSubmitting(false);
        }
    }

    const inputCls = (k) =>
            `w-full rounded-xl border bg-white px-4 py-3 text-base text-[#1E1B18] placeholder:text-[#A8A096] transition-colors duration-200 focus:border-[#A84320] focus:outline-none ${
            errors[k] ? "border-[#B3261E]" : "border-[#E4DDD3]"
        }`;

    return (
        <section id="order-form" className="bg-white py-12 md:py-24 pb-28 md:pb-24 scroll-mt-[4.25rem]">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <Reveal className="text-center max-w-xl mx-auto mb-10">
                    <p className="text-sm font-semibold text-[#A84320] tracking-wide mb-2">শেষ ধাপ</p>
                    <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1E1B18]">
                        মাত্র ১ মিনিটে অর্ডার করুন
                    </h2>
                    <p className="mt-3 text-base text-[#5C554D]">
                        ক্যাশ অন ডেলিভারি — কোনো অগ্রিম পেমেন্ট নেই। অর্ডারের পর ফোনে সবকিছু আবার নিশ্চিত করা হবে,
                        ভুল হলে তখনই ঠিক করে নিতে পারবেন।
                    </p>
                </Reveal>

                <form onSubmit={submit} noValidate className="grid lg:grid-cols-[1fr_360px] gap-6 lg:gap-8 items-start">
                    <div className="space-y-6">
                        <Reveal delay={0.05}>
                            <div className="rounded-2xl border border-[#E4DDD3] bg-[#FBF9F5] p-4 sm:p-6">
                                <StepTitle n={1} title="পছন্দের পাঞ্জাবি সিলেক্ট করুন"
                                    hint="সাইজে ট্যাপ করলেই যুক্ত হবে — একই রঙের একাধিক সাইজও নেওয়া যাবে" />
                                <ProductSelect lines={lines} setLines={setLines} stock={stock} />
                                {errors.items && (
                                    <p className="mt-3 text-sm text-[#B3261E]" data-testid="error-items">{errors.items}</p>
                                )}
                                <p className="mt-4 text-xs text-[#5C554D] bg-[#F4EFE6] border border-[#E4DDD3] rounded-xl px-4 py-3 leading-relaxed">
                                                                    অফার: ১টি = ৳৯০০ • ২টি = ৳১,৭০০ • ৩টি = ৳২,৫০০ (ফ্রি ডেলিভারি) — যেকোনো রং ও সাইজ মিলিয়ে
                                                                </p>
                            </div>
                        </Reveal>

                        <Reveal delay={0.08}>
                            <div className="rounded-2xl border border-[#E4DDD3] bg-[#FBF9F5] p-4 sm:p-6">
                                <StepTitle n={2} title="ডেলিভারি এলাকা বাছাই করুন" hint="ভুল হলে ফোনে কনফার্মেশনের সময় ঠিক করে দেওয়া হবে" />
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        ["outside_dhaka", "ঢাকার বাইরে"],
                                        ["inside_dhaka", "ঢাকার ভেতরে"],
                                    ].map(([z, label]) => {
                                        const sel = zone === z;
                                        const fee = totals.free ? "ফ্রি" : z === "inside_dhaka" ? "৳৭০" : "৳১৩০";
                                        return (
                                            <button
                                                type="button"
                                                key={z}
                                                onClick={() => { zoneManual.current = true; setZone(z); if (errors.zone) setErrors((e) => ({ ...e, zone: undefined })); }}
                                                data-testid={`zone-${z}`}
                                                aria-pressed={sel}
                                                className={`rounded-xl border-2 px-4 py-3 text-center transition-all duration-200 ${
                                                    sel ? "border-[#A84320] bg-[#FBEBE3]" : "border-[#E4DDD3] bg-white hover:border-[#C9B896]"
                                                }`}
                                            >
                                                <span className="block text-sm font-bold text-[#1E1B18]">{label}</span>
                                                <span className={`block text-xs mt-0.5 ${totals.free ? "text-[#2D5A43] font-semibold" : "text-[#5C554D]"}`}>
                                                    ডেলিভারি {fee}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {errors.zone && <p className="mt-3 text-sm text-[#B3261E]" data-testid="error-zone">{errors.zone}</p>}
                            </div>
                        </Reveal>

                        <Reveal delay={0.11}>
                            <div ref={checkoutRef} className="rounded-2xl border border-[#E4DDD3] bg-[#FBF9F5] p-4 sm:p-6">
                                <StepTitle n={3} title="নাম ও ঠিকানা লিখুন" />
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {TEXT_FIELDS.map(({ key, label, placeholder, testid, type, inputMode, autoComplete }) => (
                                        <div key={key}>
                                            <label htmlFor={key} className="block text-sm font-medium text-[#1E1B18] mb-1.5">
                                                {label} <span className="text-[#B3261E]">*</span>
                                            </label>
                                            <input
                                                id={key}
                                                type={type || "text"}
                                                inputMode={inputMode}
                                                autoComplete={autoComplete}
                                                value={form[key]}
                                                onChange={(e) => setField(key, e.target.value)}
                                                placeholder={placeholder}
                                                data-testid={testid}
                                                className={inputCls(key)}
                                            />
                                            {errors[key] && <p className="mt-1.5 text-xs text-[#B3261E]" data-testid={`error-${key}`}>{errors[key]}</p>}
                                        </div>
                                    ))}
                                    <div className="sm:col-span-2">
                                        <label htmlFor="address" className="block text-sm font-medium text-[#1E1B18] mb-1.5">
                                            সম্পূর্ণ ঠিকানা <span className="text-[#B3261E]">*</span>
                                        </label>
                                        <textarea
                                            id="address"
                                            rows={3}
                                            autoComplete="street-address"
                                            value={form.address}
                                            onChange={(e) => setField("address", e.target.value)}
                                            placeholder="বাসা/হোল্ডিং, রোড, এলাকা, থানা ও জেলা — যেমন: বাড়ি ১২, রোড ৫, মিরপুর-১০, ঢাকা"
                                            data-testid="order-form-address-input"
                                            className={inputCls("address")}
                                        />
                                        {errors.address && <p className="mt-1.5 text-xs text-[#B3261E]" data-testid="error-address">{errors.address}</p>}
                                        <p className="mt-1.5 text-[11px] text-[#8A8278]">
                                            ঠিকানায় "ঢাকা" থাকলে ডেলিভারি এলাকা নিজে থেকে ঠিক হয়ে যাবে।
                                        </p>
                                    </div>
                                </div>
                                <input type="hidden" id="country" value="বাংলাদেশ" data-testid="order-form-country-input" />
                                                                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                                                                    <div>
                                                                        <label htmlFor="email" className="block text-sm font-medium text-[#1E1B18] mb-1.5">
                                                                            ইমেইল <span className="text-[#8A8278] font-normal">(ঐচ্ছিক)</span>
                                                                        </label>
                                                                        <input id="email" type="email" autoComplete="email" value={form.email}
                                                                            onChange={(e) => setField("email", e.target.value)}
                                                                            placeholder="you@example.com" data-testid="order-form-email-input"
                                                                            className={inputCls("email")} />
                                                                        {errors.email && <p className="mt-1.5 text-xs text-[#B3261E]" data-testid="error-email">{errors.email}</p>}
                                                                    </div>
                                                                    <div>
                                                                        <label htmlFor="note" className="block text-sm font-medium text-[#1E1B18] mb-1.5">অর্ডার নোট</label>
                                                                        <input id="note" value={form.note}
                                                                            onChange={(e) => setField("note", e.target.value)}
                                                                            placeholder="বিশেষ নির্দেশনা থাকলে" data-testid="order-form-note-input"
                                                                            className={inputCls("note")} />
                                                                    </div>
                                                                </div>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.12} className="lg:sticky lg:top-24">
                        <aside className="rounded-2xl bg-[#1A1715] text-[#F4EFE6] p-5 sm:p-6" data-testid="order-summary">
                            <h3 className="font-serif text-lg font-semibold mb-4">আপনার অর্ডার</h3>
                            {totals.offer && (
                                <p className="text-xs font-semibold text-[#2D5A43] bg-[#E4EFE8] rounded-full px-3 py-1.5 mb-3 w-fit" data-testid="summary-offer">
                                    {totals.offer}
                                </p>
                            )}
                            {lines.length === 0 ? (
                                <p className="text-sm text-[#8A8278] mb-4" data-testid="summary-empty">এখনো কোনো পণ্য নির্বাচন করা হয়নি</p>
                            ) : (
                                <ul className="space-y-2 mb-4">
                                    {lines.map((l) => (
                                        <li key={`${l.color}-${l.size}`} className="flex justify-between text-sm" data-testid={`summary-line-${l.color}-${l.size}`}>
                                            <span className="text-[#B9B0A3]">{colorName(l.color)} / সাইজ {l.size}</span>
                                            <span className="text-[#F4EFE6]">× {bn(l.qty)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="border-t border-[#3A342C] pt-4 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-[#B9B0A3]">সাবটোটাল</span>
                                    <span data-testid="summary-subtotal">{lines.length ? bdt(totals.subtotal) : "—"}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-[#B9B0A3]">ডেলিভারি চার্জ</span>
                                    <span data-testid="summary-delivery">
                                        {totals.delivery === null ? "পণ্য বাছাই করুন" : totals.delivery === 0 ? "ফ্রি" : bdt(totals.delivery)}
                                    </span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-[#3A342C]">
                                    <span className="font-semibold">মোট পরিশোধযোগ্য</span>
                                    <span className="text-xl font-extrabold text-[#C49A45]" data-testid="summary-total">
                                        {totals.total === null ? "—" : bdt(totals.total)}
                                    </span>
                                </div>
                            </div>
                            <div className="mt-5 flex items-center gap-2 text-xs text-[#B9B0A3]">
                                <ShieldCheck size={15} className="text-[#7FB894] shrink-0" />
                                পেমেন্ট: ক্যাশ অন ডেলিভারি — পণ্য হাতে পেয়ে মূল্য পরিশোধ
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="order-form-submit-button"
                                                            className="mt-5 w-full rounded-full bg-[#A84320] hover:bg-[#8C3417] disabled:opacity-60 text-[#FBF9F5] font-semibold py-3.5 text-sm transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 size={17} className="animate-spin" />
                                        অর্ডার প্রসেস হচ্ছে...
                                    </>
                                ) : (
                                    "ক্যাশ অন ডেলিভারিতে অর্ডার করুন"
                                )}
                            </button>
                            {serverError && (
                                <p className="mt-3 text-sm text-[#F0A79A] text-center" data-testid="order-server-error">{serverError}</p>
                            )}
                            <p className="mt-4 text-xs text-[#8A8278] leading-relaxed text-center">
                                অর্ডারের পর আমাদের প্রতিনিধি ফোন করে চূড়ান্ত মূল্য নিশ্চিত করবেন।
                            </p>
                        </aside>
                    </Reveal>
                </form>
            </div>
        </section>
    );
}
